import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  input,
  model,
  NgZone,
  OnDestroy,
  output,
} from '@angular/core';

import { Comment } from '../../providers/bridge-api/dashboard.service';

@Directive({
  selector: '[appPinnedCommentDrag]',
})
export class PinnedCommentDragDirective implements AfterViewInit, OnDestroy {
  containerClass = input('');
  comment = model.required<Comment>();

  isDragging = output<boolean>();

  private static readonly DEBUG = false;
  private static readonly DRAG_THRESHOLD = 4;
  private static readonly REPAINT_THRESHOLD = 5;
  private static readonly DEBUG_OCTANT_COLORS = [
    '#e74c3c',
    '#e67e22',
    '#f1c40f',
    '#2ecc71',
    '#1abc9c',
    '#3498db',
    '#9b59b6',
    '#e91e63',
  ] as const;

  private elementRef = inject(ElementRef);
  private zone = inject(NgZone);
  private pendingRepaintRafId: number | null = null;
  private anchorCellEl!: HTMLElement;
  private cachedAnchorCellRect: DOMRect | null = null;
  private lastRepaintViewportX = 0;
  private lastRepaintViewportY = 0;
  private connectorSvgLine!: SVGLineElement;
  private connectorSvgRoot!: SVGSVGElement;
  private debugOctantDots: SVGCircleElement[] = [];
  private listenerAbortController = new AbortController();

  private activePointerId: number | null = null;
  private dragContainerRect!: DOMRect;
  private dragState = {
    pointerOffsetX: 0,
    pointerOffsetY: 0,
    x: 0,
    y: 0,
    pointerDownX: 0,
    pointerDownY: 0,
    isActive: false,
  };
  private naturalBounds = { left: 0, top: 0, width: 0, height: 0 };

  ngAfterViewInit(): void {
    const container = this.elementRef.nativeElement.closest(`.${this.containerClass()}`);
    this.anchorCellEl = this.elementRef.nativeElement.closest('td');

    this.attachPointerListeners(container);
    this.setpositionOfDraggedComment();
    this.checkIfOutOfBounds(container);
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.pendingRepaintRafId as number);
    this.listenerAbortController.abort();
    this.connectorSvgRoot?.remove();
  }

  private setpositionOfDraggedComment(): void {
    this.zone.runOutsideAngular(() => {
      if (this.comment().payload?.dragPosition) {
        const { x, y } = this.comment().payload!.dragPosition;
        this.dragState.x = x;
        this.dragState.y = y;
        const el = this.elementRef.nativeElement as HTMLElement;
        el.style.transform = `translate(${this.dragState.x}px, ${this.dragState.y}px)`;
      }
      this.createConnector(this.anchorCellEl);
    });
  }

  private initDragState(el: HTMLElement, e: PointerEvent, container: HTMLElement): void {
    this.dragContainerRect = container.getBoundingClientRect();
    this.cachedAnchorCellRect = this.anchorCellEl.getBoundingClientRect();

    const matrix = new DOMMatrix(getComputedStyle(el).transform);
    this.dragState.x = matrix.m41;
    this.dragState.y = matrix.m42;

    const elRect = el.getBoundingClientRect();
    this.naturalBounds = {
      left: elRect.left - this.dragState.x,
      top: elRect.top - this.dragState.y,
      width: elRect.width,
      height: elRect.height,
    };
    this.dragState.pointerOffsetX = e.clientX - elRect.left;
    this.dragState.pointerOffsetY = e.clientY - elRect.top;
    this.dragState.pointerDownX = e.clientX;
    this.dragState.pointerDownY = e.clientY;
    this.dragState.isActive = false;
  }

  private applyDrag(e: PointerEvent, el: HTMLElement): void {
    const rawX = e.clientX - this.dragState.pointerOffsetX - this.naturalBounds.left;
    const rawY = e.clientY - this.dragState.pointerOffsetY - this.naturalBounds.top;

    const minX = this.dragContainerRect.left - this.naturalBounds.left;
    const maxX = this.dragContainerRect.right - this.naturalBounds.left - this.naturalBounds.width;
    const minY = this.dragContainerRect.top - this.naturalBounds.top;
    const maxY = this.dragContainerRect.bottom - this.naturalBounds.top - this.naturalBounds.height;

    this.dragState.x = Math.min(Math.max(rawX, minX), maxX);
    this.dragState.y = Math.min(Math.max(rawY, minY), maxY);

    if (
      !this.dragState.isActive &&
      (Math.abs(e.clientX - this.dragState.pointerDownX) >
        PinnedCommentDragDirective.DRAG_THRESHOLD ||
        Math.abs(e.clientY - this.dragState.pointerDownY) >
          PinnedCommentDragDirective.DRAG_THRESHOLD)
    ) {
      this.dragState.isActive = true;
      this.zone.run(() => this.isDragging.emit(true));
    }

    el.style.transform = `translate(${this.dragState.x}px, ${this.dragState.y}px)`;
  }

  private attachPointerListeners(container: HTMLElement): void {
    const el = this.elementRef.nativeElement as HTMLElement;
    const signal = this.listenerAbortController.signal;

    this.zone.runOutsideAngular(() => {
      el.addEventListener(
        'pointerdown',
        (e: PointerEvent) => {
          if (this.activePointerId !== null) {
            return;
          }
          this.activePointerId = e.pointerId;
          el.setPointerCapture(e.pointerId);
          this.initDragState(el, e, container);
        },
        { signal }
      );

      el.addEventListener(
        'pointermove',
        (e: PointerEvent) => {
          if (e.pointerId !== this.activePointerId) {
            return;
          }
          this.applyDrag(e, el);
          if (this.pendingRepaintRafId) {
            return;
          }
          this.pendingRepaintRafId = requestAnimationFrame(() => {
            this.pendingRepaintRafId = null;
            this.repaintConnector();
          });
        },
        { signal }
      );

      el.addEventListener(
        'pointerup',
        (e: PointerEvent) => {
          if (e.pointerId !== this.activePointerId) {
            return;
          }
          this.activePointerId = null;
          this.cachedAnchorCellRect = null;
          if (!!this.dragState.isActive && this.comment().payload) {
            // This is mandatory for save triggering
            this.comment().payload!.dragPosition = { x: this.dragState.x, y: this.dragState.y };
          }
          this.dragState.isActive = false;
          el.releasePointerCapture(e.pointerId);
          this.repaintConnector();
          this.zone.run(() => setTimeout(() => this.isDragging.emit(false), 200));
        },
        { signal }
      );
    });
  }

  private createConnector(cell: HTMLElement): void {
    const ns = 'http://www.w3.org/2000/svg';
    cell.style.position = 'relative';

    const svg = document.createElementNS(ns, 'svg') as SVGSVGElement;
    svg.setAttribute(
      'style',
      'position:absolute;top:0;left:0;width:100%;height:100%;overflow:visible;pointer-events:none;z-index:1'
    );

    const line = document.createElementNS(ns, 'line') as SVGLineElement;
    line.setAttribute('stroke', '#333333');
    line.setAttribute('stroke-width', '1');
    svg.appendChild(line);

    if (PinnedCommentDragDirective.DEBUG) {
      this.debugOctantDots = PinnedCommentDragDirective.DEBUG_OCTANT_COLORS.map((color) => {
        const dot = document.createElementNS(ns, 'circle') as SVGCircleElement;
        dot.setAttribute('r', '3');
        dot.setAttribute('fill', color);
        dot.setAttribute('stroke', '#fff');
        dot.setAttribute('stroke-width', '1');
        svg.appendChild(dot);
        return dot;
      });
    }

    cell.appendChild(svg);
    this.connectorSvgLine = line;
    this.connectorSvgRoot = svg;

    this.repaintConnector();
  }

  private repaintConnector(): void {
    if (!this.connectorSvgLine) {
      return;
    }

    const cellRect = this.cachedAnchorCellRect ?? this.anchorCellEl.getBoundingClientRect();

    // During drag derive comment viewport center from cached natural bounds + current translation.
    // Outside drag fall back to a live getBoundingClientRect().
    let commentCenterViewportX: number;
    let commentCenterViewportY: number;
    let commentCenterX: number;
    let commentCenterY: number;

    if (this.activePointerId !== null) {
      const commentHalfWidth = this.naturalBounds.width / 2;
      const commentHalfHeight = this.naturalBounds.height / 2;
      commentCenterViewportX = this.naturalBounds.left + this.dragState.x + commentHalfWidth;
      commentCenterViewportY = this.naturalBounds.top + this.dragState.y + commentHalfHeight;
      commentCenterX = commentCenterViewportX - cellRect.left;
      commentCenterY = commentCenterViewportY - cellRect.top;
    } else {
      const commentRect = (this.elementRef.nativeElement as HTMLElement).getBoundingClientRect();
      commentCenterViewportX = commentRect.left + commentRect.width / 2;
      commentCenterViewportY = commentRect.top + commentRect.height / 2;
      commentCenterX = commentRect.left - cellRect.left + commentRect.width / 2;
      commentCenterY = commentRect.top - cellRect.top + commentRect.height / 2;
    }

    if (
      this.cachedAnchorCellRect !== null &&
      Math.abs(commentCenterViewportX - this.lastRepaintViewportX) <
        PinnedCommentDragDirective.REPAINT_THRESHOLD &&
      Math.abs(commentCenterViewportY - this.lastRepaintViewportY) <
        PinnedCommentDragDirective.REPAINT_THRESHOLD
    ) {
      return;
    }
    this.lastRepaintViewportX = commentCenterViewportX;
    this.lastRepaintViewportY = commentCenterViewportY;

    const commentOffsetFromCellCenterX = commentCenterX - cellRect.width / 2;
    const commentOffsetFromCellCenterY = commentCenterY - cellRect.height / 2;

    const {
      x: sourceX,
      y: sourceY,
      index: activeOctantIndex,
    } = this.getOctantSource(
      commentOffsetFromCellCenterX,
      commentOffsetFromCellCenterY,
      cellRect.width,
      cellRect.height
    );

    this.connectorSvgLine.x1.baseVal.value = sourceX;
    this.connectorSvgLine.y1.baseVal.value = sourceY;
    this.connectorSvgLine.x2.baseVal.value = commentCenterX;
    this.connectorSvgLine.y2.baseVal.value = commentCenterY;

    if (PinnedCommentDragDirective.DEBUG) {
      this.updateDebugDots(cellRect, activeOctantIndex);
    }
  }

  private getOctantSource(
    commentOffsetFromCellCenterX: number,
    commentOffsetFromCellCenterY: number,
    cellWidth: number,
    cellHeight: number
  ): { x: number; y: number; index: number } {
    const anchors: [number, number][] = [
      [cellWidth, cellHeight / 2], // 0 right-mid
      [cellWidth, cellHeight], // 1 bottom-right
      [cellWidth / 2, cellHeight], // 2 bottom-mid
      [0, cellHeight], // 3 bottom-left
      [0, cellHeight / 2], // 4 left-mid
      [0, 0], // 5 top-left
      [cellWidth / 2, 0], // 6 top-mid
      [cellWidth, 0], // 7 top-right
    ];

    const commentCenterInCellX = commentOffsetFromCellCenterX + cellWidth / 2;
    const commentCenterInCellY = commentOffsetFromCellCenterY + cellHeight / 2;

    let closestAnchorIndex = 0;
    let closestAnchorDistanceSq = Infinity;

    for (let i = 0; i < anchors.length; i++) {
      const anchorDeltaX = anchors[i][0] - commentCenterInCellX;
      const anchorDeltaY = anchors[i][1] - commentCenterInCellY;
      const distanceSq = anchorDeltaX * anchorDeltaX + anchorDeltaY * anchorDeltaY;
      if (distanceSq < closestAnchorDistanceSq) {
        closestAnchorDistanceSq = distanceSq;
        closestAnchorIndex = i;
      }
    }

    return {
      x: anchors[closestAnchorIndex][0],
      y: anchors[closestAnchorIndex][1],
      index: closestAnchorIndex,
    };
  }

  private updateDebugDots(cellRect: DOMRect, activeIndex: number): void {
    const cellWidth = cellRect.width;
    const cellHeight = cellRect.height;
    const positions: [number, number][] = [
      [cellWidth, cellHeight / 2], // right
      [cellWidth, cellHeight], // bottomRight
      [cellWidth / 2, cellHeight], // bottom
      [0, cellHeight], // bottomLeft
      [0, cellHeight / 2], // left
      [0, 0], // topLeft
      [cellWidth / 2, 0], // top
      [cellWidth, 0], // topRight
    ];
    this.debugOctantDots.forEach((dot, i) => {
      const [cx, cy] = positions[i];
      dot.setAttribute('cx', String(cx));
      dot.setAttribute('cy', String(cy));
      dot.setAttribute('r', i === activeIndex ? '5' : '3');
      dot.setAttribute('opacity', i === activeIndex ? '1' : '0.45');
    });
  }

  private checkIfOutOfBounds(container: HTMLElement): void {
    setTimeout(() => {
      const el = this.elementRef.nativeElement as HTMLElement;
      if (el.getBoundingClientRect().right > container.getBoundingClientRect().right) {
        const overflowOffsetX = -el.clientWidth;
        this.dragState.x = overflowOffsetX;
        el.style.transform = `translate(${overflowOffsetX}px, 0px)`;
      }
      this.repaintConnector();
    });
  }
}
